import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import useFetch from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, ErrorState, EmptyState, Avatar } from "@/components/common";
import Crumbs from "@/components/Crumbs";
import { saveCurrentScroll, useScrollRestore } from "@/lib/scrollRestore";
import { brl } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle, Loader2, MessageSquareWarning } from "@/components/icons";

const motivoLabel = (m) => (
  m === "sem_proxima_acao" ? "Sem próxima ação"
  : m === "sem_interacao" ? "Sem interação"
  : "Sem próxima ação + sem interação"
);

export default function SemInteracao({ embedded = false, ownerFilter = "" } = {}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, loading, error, reload } = useFetch("/equipe/sem-interacao");
  const [selected, setSelected] = useState({}); // { deal_id: true }
  const [nudge, setNudge] = useState(null); // {deal_ids:[], texto:"" }
  const [busy, setBusy] = useState(false);

  useScrollRestore(!loading && !error);

  const canNudge = ["admin", "director", "manager"].includes(user?.role);
  if (loading) return <Loading />;
  if (error) return <ErrorState onRetry={reload} />;

  const gruposRaw = data?.grupos || [];
  // Quando embutido na Carteira, o chip de vendedor filtra os grupos do próprio
  // payload — evita ida ao backend com um novo parâmetro só para essa aba.
  const grupos = ownerFilter ? gruposRaw.filter((g) => g.owner_id === ownerFilter) : gruposRaw;
  const totalSel = Object.values(selected).filter(Boolean).length;
  const totalNoEscopo = grupos.reduce((s, g) => s + g.count, 0);

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleGrupo = (grupo, force) => {
    setSelected((s) => {
      const next = { ...s };
      const allSel = grupo.items.every((it) => next[it.deal_id]);
      const target = force !== undefined ? force : !allSel;
      grupo.items.forEach((it) => { next[it.deal_id] = target; });
      return next;
    });
  };

  const openCliente = (id) => { saveCurrentScroll(); navigate(`/cliente/${id}`); };
  const openDeal = (id) => { saveCurrentScroll(); navigate(`/deal/${id}`); };

  const abrirCobranca = (deal_ids) => setNudge({ deal_ids, texto: "" });
  const submitNudge = async () => {
    if (!nudge?.texto.trim()) return toast.error("Digite o texto da solicitação");
    setBusy(true);
    try {
      const { data: r } = await api.post("/equipe/sem-interacao/cobrar-lote", {
        deal_ids: nudge.deal_ids,
        text: nudge.texto.trim(),
      });
      toast.success(`Solicitação enviada em ${r.total_criadas === 1 ? "1 oportunidade" : `${r.total_criadas} oportunidades`}`);
      setNudge(null);
      setSelected({});
      reload();
    } catch (e) { toast.error(errMsg(e, "Não foi possível enviar")); }
    finally { setBusy(false); }
  };

  const cobrarSelecionadosDoGrupo = (grupo) => {
    const ids = grupo.items.filter((it) => selected[it.deal_id]).map((it) => it.deal_id);
    if (!ids.length) return toast.error("Selecione ao menos uma oportunidade");
    abrirCobranca(ids);
  };

  return (
    <div>
      {!embedded && (
        <>
          <Crumbs items={[{ label: "Equipe", to: "/equipe" }, { label: "Sem interação" }]} testId="semint-crumbs" />
          <PageHeader
            title="Clientes sem interação"
            subtitle={`${data?.total === 1 ? "1 oportunidade" : `${data?.total || 0} oportunidades`} · sem próxima ação OU sem movimento há ${data?.dias || 7} dias`}
          />
        </>
      )}
      {embedded && (
        <div className="text-xs text-muted-foreground mb-3" data-testid="semint-embed-info">
          {totalNoEscopo === 1 ? "1 oportunidade neste escopo" : `${totalNoEscopo} oportunidades neste escopo`} · sem próxima ação OU sem movimento há {data?.dias || 7} dias
        </div>
      )}

      {grupos.length === 0 ? (
        <EmptyState icon={AlertTriangle} title="Nada pendente" subtitle="Todos os vendedores estão em dia." />
      ) : (
        <div className="space-y-4 pb-28 sm:pb-6">
          {grupos.map((g) => {
            const grupoSelCount = g.items.filter((it) => selected[it.deal_id]).length;
            const allSel = grupoSelCount === g.items.length;
            return (
              <div key={g.owner_id || "sem_owner"} className="bg-card rounded-2xl border border-black/5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] overflow-hidden" data-testid={`semint-grupo-${g.owner_id || "none"}`}>
                <div className="flex flex-wrap items-center gap-3 px-4 sm:px-5 py-3 sm:py-4 border-b border-black/5 bg-slate-50/60">
                  <Avatar name={g.owner_name} size={36} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-navy truncate">{g.owner_name}</div>
                    <div className="text-xs text-muted-foreground">{g.count === 1 ? "1 oportunidade sem interação" : `${g.count} oportunidades sem interação`}</div>
                  </div>
                  {canNudge && (
                    <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                      <button
                        onClick={() => toggleGrupo(g)}
                        data-testid={`semint-toggle-${g.owner_id || "none"}`}
                        className="h-9 px-3 rounded-lg border border-input text-xs font-medium hover:bg-slate-50 flex-1 sm:flex-initial"
                      >
                        {allSel ? "Desmarcar" : "Marcar todos"}
                      </button>
                      <button
                        onClick={() => cobrarSelecionadosDoGrupo(g)}
                        disabled={!grupoSelCount}
                        data-testid={`semint-cobrar-selecionados-${g.owner_id || "none"}`}
                        className="flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg bg-warn text-white text-[11px] sm:text-xs font-semibold hover:bg-warn/90 disabled:opacity-40 disabled:cursor-not-allowed flex-1 sm:flex-initial whitespace-nowrap"
                      >
                        <MessageSquareWarning className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">Pedir retorno{grupoSelCount ? ` (${grupoSelCount})` : ""}</span>
                      </button>
                    </div>
                  )}
                </div>
                <ul className="divide-y divide-black/5">
                  {g.items.map((it) => (
                    <li key={it.deal_id} className="flex items-start gap-2 sm:gap-3 px-4 sm:px-5 py-3 hover:bg-slate-50/50" data-testid={`semint-item-${it.deal_id}`}>
                      {canNudge && (
                        <input
                          type="checkbox"
                          checked={!!selected[it.deal_id]}
                          onChange={() => toggle(it.deal_id)}
                          data-testid={`semint-check-${it.deal_id}`}
                          className="w-4 h-4 rounded border-slate-300 text-brand focus:ring-brand shrink-0 mt-1"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <button onClick={() => it.cliente_id ? openCliente(it.cliente_id) : openDeal(it.deal_id)} className="w-full text-left">
                          {/* Item 3.11 — nome do cliente na primeira linha inteira; selo em linha própria abaixo. */}
                          <div className="font-medium text-navy break-words leading-snug" data-testid={`semint-nome-${it.deal_id}`}>{it.cliente_nome || it.titulo}</div>
                          <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                            {it.titulo} · {it.stage} · {brl(it.valor)}
                          </div>
                          <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
                            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${it.motivo === "ambos" ? "bg-bad-soft text-bad" : "bg-warn-soft text-warn"}`}>
                              {motivoLabel(it.motivo)}
                            </span>
                            {it.motivo !== "sem_proxima_acao" && (
                              <span className="text-[10px] text-muted-foreground">{it.dias_sem_interacao}d sem interação</span>
                            )}
                          </div>
                        </button>
                        {canNudge && (
                          <button
                            onClick={() => abrirCobranca([it.deal_id])}
                            data-testid={`semint-cobrar-${it.deal_id}`}
                            className="mt-2 flex items-center gap-1.5 h-9 px-3 rounded-lg border border-warn/40 bg-warn-soft text-warn text-xs font-semibold hover:bg-warn/20 w-full sm:w-auto justify-center"
                          >
                            Pedir retorno
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {totalSel > 0 && canNudge && (
        <div
          data-testid="semint-action-bar"
          className="fixed left-1/2 -translate-x-1/2 z-40 bg-navy text-white rounded-full shadow-lg px-5 py-2.5 flex items-center gap-3"
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 72px)" }}
        >
          <span className="text-sm">{totalSel === 1 ? "1 selecionada" : `${totalSel} selecionadas`}</span>
          <button
            onClick={() => abrirCobranca(Object.keys(selected).filter((k) => selected[k]))}
            data-testid="semint-cobrar-todos"
            className="h-8 px-3 rounded-full bg-warn text-white text-xs font-semibold hover:bg-warn/90"
          >
            Pedir retorno ({totalSel})
          </button>
          <button
            onClick={() => setSelected({})}
            className="text-xs text-white/80 hover:text-white underline underline-offset-2"
          >
            Limpar
          </button>
        </div>
      )}

      <Dialog open={!!nudge} onOpenChange={(o) => !o && setNudge(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pedir retorno</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            A solicitação é registrada no histórico da oportunidade e notifica o vendedor. Fica com status <b>aberta</b> até o vendedor responder.
          </p>
          <textarea
            value={nudge?.texto || ""}
            onChange={(e) => setNudge((n) => ({ ...n, texto: e.target.value }))}
            rows={4}
            placeholder="Ex.: Este cliente está sem próxima ação há mais de uma semana. Me atualize sobre o status."
            data-testid="semint-modal-texto"
            className="w-full px-3.5 py-2.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40"
          />
          <div className="text-xs text-muted-foreground">
            {nudge?.deal_ids?.length === 1 ? "1 oportunidade selecionada" : `${nudge?.deal_ids?.length} oportunidades selecionadas`}.
          </div>
          <DialogFooter>
            <button onClick={() => setNudge(null)} className="h-10 px-4 rounded-lg border border-input text-sm hover:bg-slate-50">Cancelar</button>
            <button
              onClick={submitNudge}
              disabled={busy}
              data-testid="semint-modal-enviar"
              className="h-10 px-4 rounded-lg bg-warn text-white text-sm font-semibold hover:bg-warn/90 disabled:opacity-60 flex items-center gap-2"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Enviar solicitação
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
